import { Button, Modal, Typography } from 'antd';
import { makeStyles } from '@material-ui/styles';
import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import { Checkbox } from 'antd';
import SvgFormat from 'common/helpers/svgFormat';
import event from '~helpers/mixpanel';
import Loading from '~components/loading';
import { FieldInput } from './login-formik-fields';
import { getSafeHtml } from 'common/helpers/safeHtml';
import { pageType } from '~constants/pages';

const { Title } = Typography;

export const VALIDATION_WIDTH = 216;
export const VALIDATION_HEIGHT = 60;
export const CAPTCHA_SIZE = 5;

export const captchaErrorTypeCodes = {
  CAPTCHA_CODE_ERROR: 'CAPTCHA_CODE_ERROR',
  CAPTCHA_CODE_EXPIRED: 'CAPTCHA_CODE_EXPIRED',
  MISSING_CAPTCHA_INPUT: 'MISSING_CAPTCHA_INPUT',
};

export const getCaptchaModalErrorTip = errMassageText => {
  if (errMassageText.includes(captchaErrorTypeCodes.CAPTCHA_CODE_ERROR)) {
    return 'sorry_the_code_you_entered_is_incorrect';
  }

  if (errMassageText.includes(captchaErrorTypeCodes.CAPTCHA_CODE_EXPIRED)) {
    return 'sorry_the_code_has_expired_please_refresh_to_get_a_new_code';
  }

  if (errMassageText.includes(captchaErrorTypeCodes.MISSING_CAPTCHA_INPUT)) {
    return 'sorry_the_code_you_entered_is_incorrect';
  }
};

const useStyles = makeStyles({
  root: {
    position: 'fixed',
  },
  modalContent: {
    textAlign: 'center',
    padding: '24px 60px',
  },
  button: { marginTop: 24 },
  refreshButton: { border: 0, shadow: 'none', color: '#38B2A6', marginTop: 12 },
  captchaSvg: {
    width: `${VALIDATION_WIDTH}px`,
    height: `${VALIDATION_HEIGHT}px`,
    marginTop: 48,
    marginLeft: 80,
    backgroundColor: '#F5F5F5',

    '.btn-loading__bounce': {
      backgroundColor: '#99AAB5 !important',
    },
  },
  loading: {
    paddingTop: 16,
  },
  errText: {
    textAlign: 'left',
    color: '#F25202',
  },
  noCaptchaSvg: {
    width: 36,
    height: 34,
    marginTop: 14,
  },
  brokenImgTip: {
    minHeight: 16,
    textAlign: 'center',
    marginTop: 4,
  },
  checkbox: {
    display: 'flex',
    fontSize: '12px !important',
    lineHeight: '22px !important',
    color: '#595959 !important',
    marginTop: '16px !important',

    '& > .ant-checkbox + span': {
      textAlign: 'left',
    },

    '& a': {
      textDecoration: 'underline',
    },

    '& > *:first-child': {
      width: 16,
      height: 16,
      marginTop: 4,
    },
  },
});

const CaptchaModal = ({
  authMethod,
  captchaErrorTip,
  captchaValidation,
  closeCaptchaModalAndCreateAccount,
  generateCaptcha,
  generateCaptchaLoading,
  onCloseCaptchaModal,
  openCaptchaModal,
  isLoading,
  setCaptchaErrorTip,
  showWarning = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = useState(showWarning);

  const { validation, captchaId, description } = captchaValidation?.generateCaptcha ?? {};

  const initialValues = useMemo(
    () => ({
      captchaCode: '',
    }),
    [],
  );

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        captchaCode: yup.string().max(5).required(t('this_part_cannot_be_empty')),
      }),
    [t],
  );

  const handleVerifyCaptcha = values => {
    const captchaCode = values?.captchaCode;

    if (!isLoading && captchaCode) {
      setCaptchaErrorTip();
      closeCaptchaModalAndCreateAccount(captchaCode, captchaId);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleVerifyCaptcha,
  });

  useEffect(() => {
    if (captchaErrorTip && !formik?.errors?.captchaCode) {
      formik.setErrors({ captchaCode: captchaErrorTip });
    }
  }, [captchaErrorTip, formik, setCaptchaErrorTip]);

  const handleGenerateCaptcha = () => {
    if (!isLoading) {
      generateCaptcha();
    }
  };

  const handleWarningCheckboxChange = ev => {
    setIsDisabled(!ev.target.checked);
  };

  useEffect(() => {
    event('Viewed Captcha Modal', {
      'Auth Method': authMethod,
      'Current Page Type': pageType.loginPage,
    });
  }, [authMethod]);

  return (
    <Modal
      width={560}
      visible={openCaptchaModal}
      onCancel={onCloseCaptchaModal}
      footer={null}
      maskClosable={false}
    >
      <div className={classes.modalContent}>
        <Title level={3} style={{ fontSize: '14px' }}>
          {t('for_security_reasons_please_finish_the_verification_process')}
        </Title>
        <div
          className={classNames(classes.captchaSvg, { [classes.loading]: generateCaptchaLoading })}
        >
          {generateCaptchaLoading && <Loading />}
          {!generateCaptchaLoading && validation && <div>{getSafeHtml(validation)}</div>}
          {!generateCaptchaLoading && !validation && (
            <SvgFormat type="no-captcha" className={classes.noCaptchaSvg} />
          )}
        </div>
        <div className={classes.errText}>
          {!generateCaptchaLoading &&
            !validation &&
            t(
              'oops_unable_to_generate_the_image_due_to_frequent_retries_please_try_again_in_a_few_minutes',
            )}
        </div>
        <Button
          ghost
          icon="redo"
          size="small"
          className={classes.refreshButton}
          onClick={handleGenerateCaptcha}
        >
          {t('Refresh')}
        </Button>
        <FormikProvider value={formik}>
          <FieldInput
            fieldConfig="captchaCode"
            placeholder={description || t('enter_the_code_above')}
            size="large"
            style={{ marginTop: 32 }}
            value={formik?.values?.captchaCode}
            onChange={e => {
              const value = e?.target?.value;
              setCaptchaErrorTip();
              formik?.setFieldValue('captchaCode', value);
            }}
          />
        </FormikProvider>
        {showWarning && (
          <Checkbox
            checked={!isDisabled}
            onChange={handleWarningCheckboxChange}
            className={classes.checkbox}
          >
            {t('unable_to_serve_students_searching')}
          </Checkbox>
        )}
        <Button
          className={classes.button}
          block
          loading={isLoading}
          type="primary"
          onClick={formik.handleSubmit}
          disabled={showWarning && isDisabled}
        >
          {t('submit')}
        </Button>
      </div>
    </Modal>
  );
};

CaptchaModal.propTypes = {
  authMethod: PropTypes.string,
  captchaErrorTip: PropTypes.string,
  captchaValidation: PropTypes.object,
  closeCaptchaModalAndCreateAccount: PropTypes.func.isRequired,
  generateCaptcha: PropTypes.func.isRequired,
  generateCaptchaLoading: PropTypes.bool,
  onCloseCaptchaModal: PropTypes.func.isRequired,
  openCaptchaModal: PropTypes.bool.isRequired,
  setCaptchaErrorTip: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default CaptchaModal;
